import formatDate from "astrid-helpers/src/formatDate";

import mergeDocumentData from "../../../utils/mergeDocumentData";

import getPublisherDistributionPresets from "../../distribution/utils/getPublisherDistributionPresets";

import articleChannelNeedsTakedown from "../articleChannelNeedsTakedown";
import articlePublicationStatuses from "../constants/articlePublicationStatuses";
import articleStatuses from "../constants/articleStatuses";
import getArticleChannelData from "../getArticleChannelData";

import applyArticleDistributionChannelDeliveryData from "./applyArticleDistributionChannelDeliveryData";
import applyArticleDistributionChannelPresetData from "./applyArticleDistributionChannelPresetData";

export default async function updateArticleChannelIds(
	firebase,
	{ publisher, article, data, transaction, disableTakedowns, disableDeliveryNeeded },
) {
	const mergedArticle = mergeDocumentData(article, data);

	const presets = getPublisherDistributionPresets({ article: mergedArticle, publisher });

	const isImported = !!mergedArticle.imports;

	if (data.channelIds?.length > 0) {
		const today = formatDate();

		data.channelIds = data.channelIds.filter((id) => {
			const isActivatingChannel = !article.channelIds?.includes(id);

			if (isActivatingChannel) {
				if (!presets.find((preset) => preset.channel.id === id)) {
					return false;
				}

				const takedownDate = getArticleChannelData(mergedArticle, id, "takedownDate");

				if (takedownDate && takedownDate <= today) {
					// an imported article may have been distributed outside Astrid, so the channel is
					// added anyway to let the takedown reach the distributor. Once taken down it is
					// unpublished, which keeps the next import from adding it back.
					return (
						isImported &&
						article.channels?.[id]?.publicationStatus !== articlePublicationStatuses.UNPUBLISHED
					);
				}
			}
			return true;
		});
	}

	const isGettingReady = article.status !== articleStatuses.READY && data.status === articleStatuses.READY;

	const addedChannelIds = isGettingReady
		? data.channelIds
		: data.channelIds.filter((channelId) => !article.channelIds?.includes(channelId));

	const removedChannelIds = article.channelIds?.filter((channelId) => !data.channelIds.includes(channelId)) || [];

	const channelRefs = [...new Set([...addedChannelIds, ...removedChannelIds])].map((id) =>
		firebase.firestore().collection("distributionChannels").doc(id),
	);

	const channels = await transaction.getAllDocuments(channelRefs);

	const takedownChannelIds = channels
		.filter(
			(channel) =>
				!disableTakedowns &&
				removedChannelIds?.includes(channel.id) &&
				articleChannelNeedsTakedown({ article: mergedArticle, channel }),
		)
		.map(({ id }) => id);

	data.channelIds = [...new Set([...data.channelIds, ...takedownChannelIds])].filter(Boolean); // distribution will remove from channel ids once take down is complete.

	const addedChannels = channels.filter((channel) => addedChannelIds.includes(channel.id));
	const takedownChannels = channels.filter((channel) => takedownChannelIds.includes(channel.id));

	if (takedownChannels.length) {
		data = takedownChannels.reduce(
			(data, channel) => ({
				...data,
				channels: {
					...data.channels,
					[channel.id]: {
						...(data.channels?.[channel.id] || {}),
						takedownDate: formatDate(),
					},
				},
			}),
			data,
		);
	}

	data = addedChannels.reduce((data, channel) => {
		const preset = presets.find((preset) => preset.channel.id === channel.id);

		return {
			...data,
			channels: {
				...data.channels,
				[channel.id]: {
					...applyArticleDistributionChannelDeliveryData({
						article: mergedArticle,
						channel,
						disableDeliveryNeeded,
					}),
					...applyArticleDistributionChannelPresetData({
						article: mergedArticle,
						channel,
						preset,
					}),
					...(data.channels?.[channel.id] || {}),
				},
			},
		};
	}, data);

	return data;
}
